// Field help, the shared half: the "?" button beside a label, and the context a shell provides to
// open a topic in place (the AllCode admin's HelpProvider renders the modal from its registry).
// A topic is a plain id; whether an article stands behind it is the host's to say
// (`useHost().helpHref`). The "?" renders nothing where nobody can help: no provider to open the
// topic and no article to go to. With a provider it opens the topic; with only an article it goes there.
import { createContext } from "preact";
import { useContext } from "preact/hooks";
import { useHost } from "../host/host";

/** A help topic's id ("common.costBasis"); the registry behind it is the shell's. */
export type HelpId = string;

export interface HelpServices {
  open(id: HelpId): void;
}

/** Provided by a shell that can show a topic in place; null where none can. */
export const HelpContext = createContext<HelpServices | null>(null);

/** "What is Markup?" — the accessible name of a label's help button. */
export function helpButtonLabel(label: string): string {
  return `What is ${label.trim().replace(/[?:]$/, "")}?`;
}

/** The "?" for one label. `label` is the label's words, for the name "What is Markup?". */
export function HelpButton({ topic, label }: { topic: HelpId; label: string }) {
  const help = useContext(HelpContext);
  const host = useHost();
  const href = host.helpHref(topic);
  if (help === null && href === null) return null;
  return (
    <button
      type="button"
      class="help-button"
      aria-label={helpButtonLabel(label)}
      aria-haspopup="dialog"
      data-help={topic}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (help) help.open(topic);
        else if (href !== null) host.navigate(href);
      }}
    >
      <span class="help-button-ring" aria-hidden="true">
        ?
      </span>
    </button>
  );
}
